import {
    Flex,
    Form,
    Select,
    Typography,
    Tag,
    Divider,
    List,
    Avatar,
    message,
    Popconfirm,
    Alert,
} from 'antd';
import CustomModal from '../CustomModal';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { getContacts } from './../../redux/actions/contact';
import { useCallback, useEffect, useState } from 'react';
import { CircleMinus, Users2 } from 'lucide-react';
import { addGroupMembers, getGroupMembers, removeGroupMembers } from '../../redux/actions/groups';

const { Option } = Select;

const MAX_MEMBERS = 5;

const AddMembers = ({ open, onCancel, group }) => {
    const { module, loading } = useSelector((state) => state.loadingSlice);
    const [form] = Form.useForm();
    const { contacts } = useSelector((state) => state.contactsSlice);
    const { members } = useSelector((state) => state.groupSlice);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedContacts, setSelectedContacts] = useState([]);

    const handleSearch = useCallback(
        debounce(async (searchValue) => {
            getContacts({ search: searchValue });
        }, 300),
        []
    );

    const handleChange = (val) => {
        if (members.length + val.length > MAX_MEMBERS) {
            message.warning(`A group can have max ${MAX_MEMBERS} members.`);
            return;
        }
        setSelectedIds(val);
        const selected = contacts.filter((c) => val.includes(c.id));
        setSelectedContacts(prev => [...prev, ...selected]);
    };

    const handleFinish = async (values) => {
        const { members } = values;
        const res = await addGroupMembers(group?.id, { contact_ids: members });
        if (res?.ack) {
            onCancel();
            form.resetFields();
            setSelectedIds([]);
        }
    };

    useEffect(() => {
        if (group?.id) getGroupMembers(group?.id);
    }, [group?.id]);

    const totalMembers = members.length + selectedIds.length;
    const isLimitReached = totalMembers >= MAX_MEMBERS;

    return (
        <CustomModal
            title={`Add Members ${group?.name ? `to ${group?.name}` : ''}`}
            open={open}
            onCancel={onCancel}
            okText={'Add'}
            okButtonProps={{
                disabled: !selectedIds?.length,
            }}
            onOk={() => form.submit()}
        >
            <Form form={form} onFinish={handleFinish} layout="vertical" size="large">
                <Form.Item label="Select Contacts" name="members">
                    <Select
                        mode="multiple"
                        optionLabelProp="label"
                        style={{ width: '100%' }}
                        placeholder="Select Contacts"
                        showSearch
                        allowClear
                        value={selectedIds}
                        onChange={handleChange}
                        disabled={isLimitReached}
                        filterOption={(input, option) =>
                            String(option?.['data-name'] || '')
                                .toLowerCase()
                                .includes(input.toLowerCase()) ||
                            String(option?.['data-phone'] || '')
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        onSearch={handleSearch}
                        tagRender={({ value, onClose }) => {
                            const contact = selectedContacts.find((c) => c.id === value);
                            return (
                                <Tag
                                    closable
                                    onClose={onClose}
                                    style={{ padding: '2px 6px', margin: 3 }}
                                    bordered={false}
                                >
                                    <Typography.Text strong>{contact?.name}</Typography.Text>{' '}
                                    <Typography.Text type="secondary">({contact?.phone})</Typography.Text>
                                </Tag>
                            );
                        }}
                    >
                        {contacts
                            ?.filter(
                                (c) => !selectedIds.includes(c?.id) && !members.some((m) => m.contact_id === c.id)
                            )
                            .map((c) => (
                                <Option
                                    key={c?.id}
                                    value={c?.id}
                                    label={`${c?.name} (${c?.phone})`}
                                    data-phone={c?.phone}
                                    data-name={c?.name}
                                    disabled={isLimitReached}
                                >
                                    <Flex vertical gap={1}>
                                        <Typography.Text>{c?.name}</Typography.Text>
                                        <Typography.Text type="secondary">{c?.phone}</Typography.Text>
                                    </Flex>
                                </Option>
                            ))}
                    </Select>
                </Form.Item>

                {isLimitReached && (
                    <Alert
                        message={`Maximum ${MAX_MEMBERS} members allowed in a group.`}
                        type="warning"
                        showIcon
                        closable
                        style={{ marginTop: 12 }}
                    />
                )}
            </Form>
            <Divider />
            <List
                header={
                    <Typography.Title level={5} style={{ margin: 0 }}>
                        Added Members
                    </Typography.Title>
                }
                bordered
                loading={module === 'GET_GROUP_MEMBERS' && loading}
                itemLayout="horizontal"
                dataSource={members}
                renderItem={(item) => (
                    <List.Item
                        actions={[
                            <Popconfirm
                                title="Are you sure to remove this member?"
                                onConfirm={() => removeGroupMembers(group?.id, { contact_ids: [item.contact_id] })}
                                okText="Yes"
                                cancelText="No"
                            >
                                <CircleMinus cursor="pointer" color="var(--danger-color)" />
                            </Popconfirm>,
                        ]}
                    >
                        <List.Item.Meta
                            avatar={<Avatar icon={<Users2 size={14} />} />}
                            title={item?.name}
                            description={item?.phone}
                        />
                    </List.Item>
                )}
            />
        </CustomModal>
    );
};

export default AddMembers;
